import { useEffect, useState, type FormEvent } from 'react';
import { RefreshCw } from 'lucide-react';
import type { UnifiConsole } from '@iot-dashboard/shared';
import { useDeleteIntegration, useIntegration, useSyncNow, useTestIntegration, useUpdateIntegration } from '../../api/hooks';
import { FormRow, Toggle } from '../../components/Form';
import { ConfirmDialog } from '../../components/Modal';
import { Panel } from '../../components/Panel';
import { StatusDot } from '../../components/StatusDot';
import { ErrorState, Loading, errorMessage } from '../../components/States';
import { TimeAgo } from '../../components/TimeAgo';
import { useToast } from '../../components/Toast';
import { PageHeader } from '../../layout/PageHeader';
import { useTenantContext } from '../../layout/TenantLayout';

export function IntegrationsPage() {
  const { tenantId, canConfigure } = useTenantContext();
  const integration = useIntegration(tenantId);
  const update = useUpdateIntegration(tenantId);
  const test = useTestIntegration(tenantId);
  const sync = useSyncNow(tenantId);
  const del = useDeleteIntegration(tenantId);
  const toast = useToast();
  const [apiKey, setApiKey] = useState('');
  const [enabled, setEnabled] = useState(false);
  const [interval, setIntervalMinutes] = useState(5);
  const [consoleIds, setConsoleIds] = useState<string[]>([]);
  const [consoles, setConsoles] = useState<UnifiConsole[] | null>(null);
  const [testMessage, setTestMessage] = useState<{ ok: boolean; text: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmRemove, setConfirmRemove] = useState(false);

  useEffect(() => {
    if (integration.data) {
      setEnabled(integration.data.enabled);
      setIntervalMinutes(integration.data.syncIntervalMinutes);
      setConsoleIds(integration.data.consoleIds);
    }
  }, [integration.data]);

  if (integration.isPending) return <Loading />;
  if (integration.isError) return <ErrorState error={integration.error} retry={() => integration.refetch()} />;
  const cfg = integration.data;

  const runTest = async () => {
    setTestMessage(null);
    try {
      const result = await test.mutateAsync(apiKey.trim() || undefined);
      setTestMessage({ ok: result.ok, text: result.message });
      setConsoles(result.consoles);
    } catch (err) {
      setTestMessage({ ok: false, text: errorMessage(err) });
    }
  };

  const save = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await update.mutateAsync({ apiKey: apiKey.trim() || undefined, enabled, syncIntervalMinutes: interval, consoleIds });
      setApiKey('');
      toast.push('Integration saved. A sync has been queued.', 'success');
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  const copy = (text: string) => navigator.clipboard?.writeText(text).then(() => toast.push('Copied to clipboard', 'success'));

  return (
    <>
      <PageHeader
        title="Integration"
        subtitle="The dashboard reads this tenant's sensors and cameras from its consoles over their cloud connection. No VPN or inbound firewall rule is needed."
        actions={canConfigure && cfg.hasApiKey ? (
          <button type="button" className="btn" disabled={sync.isPending || !cfg.enabled} onClick={() => sync.mutateAsync().then(() => toast.push('Sync queued', 'success')).catch((err) => toast.push(errorMessage(err), 'error'))}>
            <RefreshCw size={15} /> Sync now
          </button>
        ) : null}
      />

      <div className="stat-row">
        <div className="stat stat--accent"><div className="stat__label">API key</div><div className="stat__value" style={{ fontSize: 18 }}>{cfg.hasApiKey ? `•••• ${cfg.apiKeyLast4}` : 'Not set'}</div></div>
        <div className={`stat stat--${cfg.enabled ? 'good' : 'neutral'}`}><div className="stat__label">Polling</div><div className="stat__value" style={{ fontSize: 18 }}>{cfg.enabled ? `Every ${cfg.syncIntervalMinutes} min` : 'Disabled'}</div></div>
        <div className={`stat stat--${cfg.lastSyncStatus === 'ok' ? 'good' : cfg.lastSyncStatus === 'error' ? 'critical' : 'neutral'}`}><div className="stat__label">Last sync</div><div className="stat__value" style={{ fontSize: 18 }}><StatusDot status={cfg.lastSyncStatus} /></div><div className="stat__hint"><TimeAgo iso={cfg.lastSyncAt} />{cfg.lastSyncDeviceCount !== undefined ? ` · ${cfg.lastSyncDeviceCount} sensors` : ''}</div></div>
        <div className="stat"><div className="stat__label">Consoles</div><div className="stat__value" style={{ fontSize: 18 }}>{cfg.consoleIds.length === 0 ? 'All supported' : cfg.consoleIds.length}</div></div>
      </div>
      {cfg.lastSyncError ? <div className="alert-banner alert-banner--warning"><div><strong>Last sync problem:</strong> {cfg.lastSyncError}</div></div> : null}

      <div className="grid-2">
        <form onSubmit={save}>
          <Panel title="Connection" footer={canConfigure ? <><button type="button" className="btn" disabled={test.isPending || (!apiKey && !cfg.hasApiKey)} onClick={runTest}>{test.isPending ? 'Testing…' : 'Test connection'}</button><button type="submit" className="btn btn--primary" disabled={update.isPending}>{update.isPending ? 'Saving…' : 'Save changes'}</button></> : undefined}>
            <div className="form">
              {error ? <div className="form-error">{error}</div> : null}
              {testMessage ? <div className={testMessage.ok ? 'form-success' : 'form-error'}>{testMessage.text}</div> : null}
              <FormRow label="API key" htmlFor="apikey" hint="Created by the client in their cloud account under Settings → API Keys (or use an organisation key that covers this client's consoles). Stored encrypted in AWS Secrets Manager; never shown again.">
                <input id="apikey" className="input mono" type="password" autoComplete="off" disabled={!canConfigure} placeholder={cfg.hasApiKey ? 'Leave blank to keep the current key' : 'Paste the API key'} value={apiKey} onChange={(e) => setApiKey(e.target.value)} />
              </FormRow>
              <FormRow label="Consoles" hint="Only these consoles are treated as this tenant's sites; unticking one removes its site, sensors and cameras from the tenant at the next sync. Required when one organisation key spans several clients. Empty = every supported console (one that manages sensors and cameras) the key can see.">
                {consoles ? (
                  <div className="checklist">
                    {consoles.map((c) => (
                      <label key={c.id}>
                        <input type="checkbox" disabled={!canConfigure} checked={consoleIds.includes(c.id)} onChange={(e) => setConsoleIds(e.target.checked ? [...consoleIds, c.id] : consoleIds.filter((x) => x !== c.id))} />
                        <StatusDot status={c.isOnline === false ? 'offline' : 'online'} label={false} />
                        <span className="grow">{c.name} <span className="muted small">{c.ipAddress ?? ''}</span></span>
                        <span className="small muted">{c.hasProtect ? 'Supported' : 'Not supported'}</span>
                      </label>
                    ))}
                  </div>
                ) : (
                  <div className="secondary">
                    <div>{consoleIds.length ? <span className="mono small">{consoleIds.join(', ')}</span> : 'All supported consoles.'}</div>
                    <div className="muted small">Use "Test connection" to list the consoles the key can see.</div>
                  </div>
                )}
              </FormRow>
              <FormRow label="Polling interval" htmlFor="interval" hint="Minutes between reads of every sensor. Webhooks from the consoles' Alarm Manager trigger an immediate refresh on top of this.">
                <select id="interval" className="select" disabled={!canConfigure} value={interval} onChange={(e) => setIntervalMinutes(Number(e.target.value))}>
                  {[5, 10, 15, 30, 60].map((m) => <option key={m} value={m}>{m} minutes</option>)}
                </select>
              </FormRow>
              <FormRow label="Enabled"><Toggle checked={enabled} disabled={!canConfigure} onChange={setEnabled} label={enabled ? 'Polling is on' : 'Polling is off'} /></FormRow>
            </div>
          </Panel>
        </form>

        <div className="stack">
          <Panel title="Real-time events (webhook)">
            {cfg.webhookUrl ? (
              <div className="form">
                <p className="secondary">Point each console's <strong>Alarm Manager</strong> at this URL to log sensor events the moment they happen and trigger an immediate sync.</p>
                <div className="copy-field">
                  <input className="input" readOnly value={cfg.webhookUrl} aria-label="Webhook URL" />
                  <button type="button" className="btn" onClick={() => copy(cfg.webhookUrl!)}>Copy</button>
                </div>
                <ol className="steps small secondary">
                  <li>On the console open <strong>Settings → Alarm Manager → Create alarm</strong>.</li>
                  <li>Choose the trigger (water leak, door opened, motion, temperature…) and the sensors.</li>
                  <li>Add an action of type <strong>Webhook → Custom webhook</strong>, paste the URL and set the method to <strong>POST</strong>.</li>
                  <li>
                    For licence plates, create a second alarm triggered on <strong>Known vehicles</strong> and
                    <strong> Unknown vehicles</strong> with the same webhook action. The console only pushes plate
                    reads, so without this alarm the Licence plates page stays empty. Turn on
                    <strong> Use thumbnails</strong> in it to see a picture of each vehicle.
                  </li>
                </ol>
                {canConfigure ? <div><button type="button" className="btn btn--sm" disabled={update.isPending} onClick={() => update.mutateAsync({ rotateWebhookToken: true }).then(() => toast.push('Webhook URL rotated. Update the Alarm Manager action.', 'success'))}>Rotate webhook URL</button></div> : null}
              </div>
            ) : (
              <p className="secondary">Save an API key first to get this tenant's webhook URL{canConfigure ? '' : ' (administrators only)'}.</p>
            )}
          </Panel>
          <Panel title="How it works">
            <ol className="steps secondary small">
              <li>Every {cfg.syncIntervalMinutes} minutes the platform lists this tenant's consoles through the cloud API.</li>
              <li>From each supported console it reads the sensors and cameras through the cloud connection (console firmware 5.0.3 or later).</li>
              <li>Readings are stored for 90 days, alert profiles are evaluated and the dashboard updates.</li>
            </ol>
            {canConfigure && cfg.hasApiKey ? <button type="button" className="btn btn--danger btn--sm" onClick={() => setConfirmRemove(true)}>Remove integration</button> : null}
          </Panel>
        </div>
      </div>
      {confirmRemove ? (
        <ConfirmDialog title="Remove integration" message="This deletes the stored API key and stops polling. Sensors and history stay until they age out." confirmLabel="Remove" danger busy={del.isPending} onConfirm={() => del.mutateAsync().then(() => { toast.push('Integration removed', 'success'); setConfirmRemove(false); setConsoles(null); }).catch((err) => toast.push(errorMessage(err), 'error'))} onClose={() => setConfirmRemove(false)} />
      ) : null}
    </>
  );
}
